import PageHero from "@/components/PageHero";
import LiveWeatherWidget from "@/components/LiveWeatherWidget";
import RegionCard from "@/components/RegionCard";
import EditorialMarquee from "@/components/EditorialMarquee";
import TranslatedProse from "@/components/TranslatedProse";
import { motion } from "framer-motion";

const HERO_ART = "https://customer-assets-m6fa6gv7.emergentagent.net/job_green-timeline/artifacts/5b3vbv8p_weather-illustration.jpg";

const IMG = {
  storm: "https://images.pexels.com/photos/4028958/pexels-photo-4028958.jpeg?auto=compress&cs=tinysrgb&w=1800",
  sunrise: "https://images.unsplash.com/photo-1519861531473-9200262188bf?w=1800&auto=format&fit=crop",
  snow: "https://images.unsplash.com/photo-1483664852095-d6cc6870702d?w=1800&auto=format&fit=crop",
  rainbow: "https://images.unsplash.com/photo-1502082553048-f009c37129b9?w=1800&auto=format&fit=crop",
  fog: "https://images.unsplash.com/photo-1487621167305-5d248087c724?w=1800&auto=format&fit=crop",
  clouds: "https://images.unsplash.com/photo-1534088568595-a066f410bcda?w=1800&auto=format&fit=crop",
  coast: "https://images.pexels.com/photos/36767372/pexels-photo-36767372.jpeg?auto=compress&cs=tinysrgb&w=1800",
  lightning: "https://images.unsplash.com/photo-1605727216801-e27ce1d0cc28?w=1800&auto=format&fit=crop",
  farm: "https://images.unsplash.com/photo-1500382017468-9049fed747ef?w=1800&auto=format&fit=crop",
};

function Chapter({ eyebrow, title, id, children }) {
  return (
    <section id={id} className="max-w-4xl mx-auto px-6 md:px-10 py-16 md:py-24">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-15%" }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
      >
        <div className="font-mono-tech text-[10px] tracking-[0.4em] uppercase text-[color:var(--np-muted)] mb-4">{eyebrow}</div>
        <h2 className="font-serif-display text-5xl md:text-7xl leading-[0.95] tracking-tighter mb-8">{title}</h2>
        <div className="space-y-6 text-lg leading-relaxed text-[color:var(--np-text-2)]">{children}</div>
      </motion.div>
    </section>
  );
}

function ImagePlate({ src, alt, caption, aspect = "aspect-[16/9]" }) {
  return (
    <motion.figure
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-10%" }}
      transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
      className="max-w-6xl mx-auto px-6 md:px-10 my-16 md:my-20"
      data-testid={`weather-image-${alt.replace(/\s+/g, "-").toLowerCase()}`}
    >
      <div className={`relative w-full ${aspect} overflow-hidden bg-[color:var(--np-bg-2)] shadow-[0_40px_100px_-40px_rgba(228,87,46,0.35)]`}>
        <img src={src} alt={alt} className="w-full h-full object-cover" loading="lazy" />
      </div>
      {caption && (
        <figcaption className="mt-3 font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[color:var(--np-muted)]">
          {caption}
        </figcaption>
      )}
    </motion.figure>
  );
}

const MISSION = [
  { verb: "Observe" }, { verb: "Record" }, { verb: "Share" }, { verb: "Learn" }, { verb: "Preserve" },
];

const NOW = [
  { e: "☀️", t: "Sunshine and clear skies" },
  { e: "☁️", t: "Cloud cover and changing skies" },
  { e: "🌧", t: "Rainfall and thunderstorms" },
  { e: "❄️", t: "Snow and winter conditions" },
  { e: "🌪", t: "Tornadoes and severe storms" },
  { e: "🌊", t: "Hurricanes, cyclones, and typhoons" },
  { e: "🌫", t: "Fog and mist" },
  { e: "🌬", t: "Wind conditions" },
  { e: "🌈", t: "Rainbows and atmospheric phenomena" },
  { e: "🌡", t: "Temperature and humidity" },
  { e: "🌊", t: "Ocean and coastal conditions" },
  { e: "🔥", t: "Heatwaves and wildfire conditions" },
  { e: "🌬", t: "Air quality and visibility" },
  { e: "🌅", t: "Beautiful sunrises and sunsets" },
];

const MAJOR = ["Hurricanes","Cyclones","Typhoons","Tornadoes","Floods","Flash floods","Heatwaves","Cold waves","Snowstorms","Blizzards","Ice storms","Droughts","Wildfires","Dust storms","Hailstorms","Lightning outbreaks","Storm surges","Coastal flooding","Atmospheric rivers"];
const DAILY_OBS = ["Daily temperatures","Rainfall","Snowfall","Humidity","Wind speed","Air pressure","Cloud cover","UV Index","Sunrise & sunset times","Air Quality Index","Visibility","Seasonal conditions"];
const CLIMATE_LENS = ["Seasonal changes","Long-term temperature trends","Rainfall patterns","Changing snowfall","Storm frequency","Drought cycles","Heatwave occurrences","Flood history","Regional climate variability","Environmental changes"];
const COMMUNITY_SHARE = ["Weather photographs","Sunrise & sunset images","Cloud formations","Rainfall observations","Snowfall reports","Storm videos","Flood photographs","Rainbow sightings","Lightning images","Wildlife responding to weather","Local weather stories","Historic weather memories","Seasonal changes","Community experiences during storms"];
const COMMUNITY_KNOW = ["First snowfall of the season","Flower blooms after spring rains","River levels after storms","Heatwave experiences","Fog rolling into a valley","Birds gathering before migration","Coastal storm impacts","Mountain snowfall","Changing autumn colours","Local flooding","Ice formation","Dust storms","Exceptional sunsets after weather systems"];
const AUDIENCES = ["Students & educators","Families exploring weather history","Meteorologists","Climate researchers","Environmental scientists","Historians","Journalists","Emergency planners","Photographers","Nature enthusiasts","Conservation orgs","Community groups"];
const CONNECTIONS = ["Landscapes","Rivers & lakes","Mountains","Forests","Wildlife","Birds","Oceans","Plants","Flowers","Agriculture","Ecosystems","Climate Change","Environmental History"];

export default function Weather() {
  return (
    <TranslatedProse cacheKey="weather">
      <PageHero
        eyebrow="/ Weather · Today's Weather · Tomorrow's History"
        title="Every cloud tells a story."
        subtitle="Documenting today's weather. Preserving tomorrow's history. Every observation becomes part of Earth's living record."
        image={IMG.storm}
      />

      <EditorialMarquee terms={["OBSERVE", "RECORD", "SHARE", "LEARN", "PRESERVE", "EVERY CLOUD · EVERY STORM · EVERY DAY"]} />

      {/* Featured illustration */}
      <motion.figure
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-10%" }}
        transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
        className="max-w-6xl mx-auto px-6 md:px-10 pt-24 md:pt-32"
        data-testid="weather-hero-illustration"
      >
        <div className="relative w-full overflow-hidden bg-[color:var(--np-bg-2)] shadow-[0_60px_140px_-40px_rgba(228,87,46,0.45)]">
          <img
            src={HERO_ART}
            alt="Weather & Weather Almanac — Today's Weather. Tomorrow's History."
            className="w-full h-auto"
          />
        </div>
        <figcaption className="mt-4 font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[color:var(--np-muted)]">
          Current weather around the world · Weather Almanac · Be part of the record
        </figcaption>
      </motion.figure>

      <Chapter eyebrow="/01 · Welcome" title="Welcome to Weather.">
        <p>
          Weather influences every aspect of life on Earth. It shapes our seasons, nourishes our forests, fills our rivers, supports agriculture, influences wildlife migration, and affects the daily lives of billions of people. Every sunrise, every rainfall, every snowfall, every thunderstorm, and every calm day becomes part of the continuous story of our planet.
        </p>
        <p>
          The Weather section of NaturePulse.cloud, part of the SocialPulse ecosystem, is dedicated to documenting weather around the world — past, present, and future.
        </p>
        <p>
          More than a weather service, NaturePulse is building one of the world's largest living weather archives, preserving daily weather observations, historic events, environmental changes, and community experiences for generations to come. The full historical record lives on our companion <a href="/weather-almanac" className="text-[color:var(--np-brand)] underline decoration-2 underline-offset-4">Weather Almanac page</a>.
        </p>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-black/10 mt-8">
          {MISSION.map(m => (
            <div key={m.verb} className="bg-[color:var(--np-bg)] p-5 text-center">
              <div className="font-mono-tech text-[10px] tracking-[0.35em] uppercase text-[color:var(--np-brand)] mb-2">Mission</div>
              <div className="font-serif-display text-3xl leading-none">{m.verb}.</div>
            </div>
          ))}
        </div>
      </Chapter>

      {/* Live now */}
      <section className="py-16 md:py-24 px-6 md:px-10 max-w-[1600px] mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-10">
          <div className="md:col-span-5">
            <div className="font-mono-tech text-[10px] tracking-[0.4em] uppercase text-[color:var(--np-muted)] mb-4">/02 · Live now</div>
            <h2 className="font-serif-display text-5xl md:text-7xl leading-[0.95] tracking-tighter">Right now, on Earth.</h2>
          </div>
          <p className="md:col-span-6 md:col-start-7 self-end text-lg text-[color:var(--np-text-2)]">
            Cycle through five cities across five continents. Every reading is live — temperature, humidity, UV, wind, and the US EPA AQI.
          </p>
        </div>
        <div className="max-w-2xl">
          <LiveWeatherWidget />
        </div>
      </section>

      <RegionCard />

      <EditorialMarquee terms={["+42°C SEVILLA", "AQI 380 DELHI", "TROPICAL DEPRESSION BAY OF BENGAL", "-38°C YAKUTSK", "STORM SURGE 3.2M", "AURORA KP-INDEX 7"]} />

      <Chapter eyebrow="/03 · Today's weather around the world" title="A world of weather. One place to explore.">
        <p>NaturePulse continuously showcases current weather conditions from cities, towns, villages, mountains, coastlines, deserts, forests, islands, and remote regions across the globe. Visitors can explore weather happening right now, including:</p>
        <ul className="grid grid-cols-1 md:grid-cols-2 gap-y-2 gap-x-6 text-[color:var(--np-text)] font-serif-display text-lg">
          {NOW.map(x => (
            <li key={x.t} className="flex items-baseline gap-3">
              <span className="text-xl leading-none">{x.e}</span> {x.t}
            </li>
          ))}
        </ul>
        <p>Weather is constantly changing, and NaturePulse allows everyone to witness the diversity of Earth's atmosphere as it unfolds each day.</p>
      </Chapter>

      <ImagePlate src={IMG.lightning} alt="Lightning storm" caption="Lightning outbreaks · every strike a fingerprint of atmospheric energy" />

      <Chapter eyebrow="/04 · The Global Weather Almanac" title="Every event, a permanent record.">
        <p>
          The <a href="/weather-almanac" className="text-[color:var(--np-brand)] underline decoration-2 underline-offset-4">Weather Almanac</a> is the heart of NaturePulse — a permanent historical archive documenting weather events from every country and region around the world.
        </p>
        <p>Each weather event becomes a comprehensive record containing:</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-black/10">
          {["Event title","Date and time","Geographic location","Country, state, region, city","GPS coordinates","Weather category","Scientific observations","Historical context","Weather maps","Radar imagery (where available)","Satellite imagery","Photographs","Community photos","Videos","News articles","Official weather advisories","Educational resources","Related weather events","Community discussions"].map(x => (
            <div key={x} className="bg-[color:var(--np-bg)] p-4 font-serif-display text-lg">{x}</div>
          ))}
        </div>
        <p>Every event becomes searchable and connected to the places and communities it affected.</p>
        <a href="/weather-almanac" className="inline-flex items-center gap-2 mt-4 font-mono-tech text-[11px] tracking-[0.3em] uppercase border border-black/20 hover:bg-[color:var(--np-text)] hover:text-[color:var(--np-bg)] px-5 py-3 transition-colors" data-testid="weather-open-almanac-btn">
          Open the Weather Almanac →
        </a>
      </Chapter>

      <ImagePlate src={IMG.snow} alt="Snow-covered village" caption="Snow · watersheds fed, seasons recorded" />

      <Chapter eyebrow="/05 · Every weather event matters" title="Extraordinary. Everyday. Equal.">
        <p>NaturePulse documents both extraordinary and everyday weather.</p>
        <div className="font-mono-tech text-[10px] tracking-[0.35em] uppercase text-[color:var(--np-brand)] mt-4 mb-3">Major events</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-black/10">
          {MAJOR.map(x => (
            <div key={x} className="bg-[color:var(--np-bg)] p-3 font-serif-display text-lg">{x}</div>
          ))}
        </div>
        <p>Daily observations are equally important because long-term understanding comes from consistent records. These include:</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-black/10">
          {DAILY_OBS.map(x => (
            <div key={x} className="bg-[color:var(--np-bg)] p-3 font-serif-display text-lg">{x}</div>
          ))}
        </div>
        <p className="font-serif-display text-2xl text-[color:var(--np-text)] italic">Today's ordinary weather becomes tomorrow's history.</p>
      </Chapter>

      <ImagePlate src={IMG.clouds} alt="Cloud formations" caption="Cloud formations · atmosphere in motion" />

      <Chapter eyebrow="/06 · Understanding climate through weather" title="Days become decades.">
        <p>Individual weather events may last hours or days, but together they create long-term climate records. By documenting weather every day, NaturePulse helps people observe:</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-black/10">
          {CLIMATE_LENS.map(x => (
            <div key={x} className="bg-[color:var(--np-bg)] p-4 font-serif-display text-lg">{x}</div>
          ))}
        </div>
        <p>These records provide valuable context for understanding how weather and climate interact over time.</p>
      </Chapter>

      <ImagePlate src={IMG.sunrise} alt="Sunrise over ocean" caption="Sunrise · one page of a chronicle that never ends" />

      <Chapter eyebrow="/07 · More than forecasts" title="Yesterday matters. Today counts.">
        <p>NaturePulse is not only about forecasting tomorrow's weather. It is about preserving yesterday's weather and documenting today's observations so future generations can better understand environmental change.</p>
        <p>Imagine being able to explore:</p>
        <ul className="space-y-2 text-[color:var(--np-text)] font-serif-display text-xl">
          {[
            "What was the weather in your hometown fifty years ago?",
            "When did your city experience its hottest summer?",
            "How often have floods affected your community?",
            "Which storms shaped local history?",
            "How have rainfall patterns changed over decades?",
          ].map(x => (
            <li key={x} className="flex items-baseline gap-3"><span className="w-1.5 h-1.5 rounded-full bg-[color:var(--np-brand)] inline-block" />{x}</li>
          ))}
        </ul>
        <p>The Weather Almanac is designed to answer these questions through a growing historical archive.</p>
      </Chapter>

      <ImagePlate src={IMG.fog} alt="Fog rolling in" caption="Fog · quietly reshaping the visible world" />

      <Chapter eyebrow="/08 · Built by the world" title="Everyone can contribute.">
        <p>NaturePulse believes that everyone can contribute to our understanding of weather. Whether you are a professional meteorologist, a photographer, a farmer, a teacher, a student, a traveler, or simply someone who enjoys looking at the sky, your observations matter.</p>
        <p>We invite people everywhere to share:</p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-px bg-black/10">
          {COMMUNITY_SHARE.map(x => (
            <div key={x} className="bg-[color:var(--np-bg)] p-3 font-serif-display text-lg">{x}</div>
          ))}
        </div>
        <p>Every contribution helps build a richer and more complete environmental record.</p>
      </Chapter>

      <ImagePlate src={IMG.rainbow} alt="Rainbow after storm" caption="Rainbows · the calm on the other side of the storm" />

      <Chapter eyebrow="/09 · Powered by community knowledge" title="Ordinary people. Extraordinary records.">
        <p>Some of the world's most valuable weather records begin with ordinary people paying attention to extraordinary moments. Community contributions may include:</p>
        <ul className="grid grid-cols-1 md:grid-cols-2 gap-y-2 gap-x-6 text-[color:var(--np-text)] font-serif-display text-lg">
          {COMMUNITY_KNOW.map(x => (
            <li key={x} className="flex items-baseline gap-3"><span className="w-1.5 h-1.5 rounded-full bg-[color:var(--np-live)] inline-block" />{x}</li>
          ))}
        </ul>
        <p>These observations provide local perspectives that complement scientific data and enrich the historical record.</p>
      </Chapter>

      <Chapter eyebrow="/10 · For everyone" title="Science, education, research.">
        <p>The Weather Almanac is designed as a resource for everyone. It supports:</p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-px bg-black/10">
          {AUDIENCES.map(x => (
            <div key={x} className="bg-[color:var(--np-bg)] p-3 font-serif-display text-lg">{x}</div>
          ))}
        </div>
        <p>By combining official weather information with community observations, historical records, and multimedia, NaturePulse creates a valuable knowledge base for learning and research.</p>
      </Chapter>

      <ImagePlate src={IMG.coast} alt="Coastal storm" caption="Storms reshape coastlines · every landfall a new line on the map" />

      <Chapter eyebrow="/11 · Connected to nature" title="Weather doesn't exist in isolation.">
        <p>Every weather event influences nature.</p>
        <ul className="grid grid-cols-1 md:grid-cols-2 gap-y-2 gap-x-6 text-[color:var(--np-text)] font-serif-display text-lg">
          {["Rain nourishes forests.","Snow feeds rivers.","Wind disperses seeds.","Storms reshape coastlines.","Heat affects wildlife.","Changing seasons guide migration."].map(x => (
            <li key={x} className="flex items-baseline gap-3"><span className="w-1.5 h-1.5 rounded-full bg-[color:var(--np-brand)] inline-block" />{x}</li>
          ))}
        </ul>
        <p>That is why NaturePulse connects the Weather Almanac with:</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-black/10">
          {CONNECTIONS.map(x => (
            <div key={x} className="bg-[color:var(--np-bg)] p-3 font-serif-display text-lg">{x}</div>
          ))}
        </div>
        <p>Together, they tell the complete story of our living planet.</p>
      </Chapter>

      <ImagePlate src={IMG.farm} alt="Farmland in changing seasons" caption="Seasons · every harvest a data point" />

      <Chapter eyebrow="/12 · Earth's weather memory" title="Imagine a future.">
        <p>Imagine a future where anyone can explore the weather history of any place on Earth.</p>
        <ul className="space-y-2 text-[color:var(--np-text)] font-serif-display text-xl">
          {[
            "A child learning about a hurricane that shaped their hometown.",
            "A researcher studying decades of rainfall.",
            "A farmer comparing seasonal changes.",
            "A family remembering the snowstorm that brought everyone together.",
            "A community preserving photographs of a remarkable sunrise after a storm.",
          ].map(x => (
            <li key={x} className="flex items-baseline gap-3"><span className="w-1.5 h-1.5 rounded-full bg-[color:var(--np-brand)] inline-block" />{x}</li>
          ))}
        </ul>
        <p>This is the vision of the NaturePulse Weather Almanac — a continuously growing digital memory of Earth's atmosphere.</p>
      </Chapter>

      <Chapter eyebrow="/13 · Join us" title="Join the global weather community.">
        <p>NaturePulse invites you to become part of this worldwide effort.</p>
        <ul className="space-y-2 text-[color:var(--np-text)] font-serif-display text-xl">
          {["Observe your surroundings.","Photograph the sky.","Record the rain.","Share your storm stories.","Capture the beauty of a sunrise.","Document unusual weather.","Contribute local knowledge.","Help preserve today's weather for tomorrow's generations."].map(x => (
            <li key={x} className="flex items-baseline gap-3"><span className="w-1.5 h-1.5 rounded-full bg-[color:var(--np-live)] inline-block" />{x}</li>
          ))}
        </ul>
        <p>Because every observation matters, every location has a story, and every day adds another page to the history of our planet.</p>
      </Chapter>

      {/* Signature */}
      <section className="max-w-5xl mx-auto px-6 md:px-10 pt-8 pb-32 md:pb-40 text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1 }}
        >
          <div className="font-mono-tech text-[10px] tracking-[0.4em] uppercase text-[color:var(--np-muted)] mb-6">/ Signature</div>
          <h2 className="font-serif-display text-6xl md:text-8xl leading-[0.9] tracking-tighter mb-6">
            <em className="text-[color:var(--np-brand)]">NaturePulse.</em>
          </h2>
          <p className="font-serif-display text-2xl md:text-3xl italic text-[color:var(--np-text)] max-w-3xl mx-auto leading-tight mb-4">
            Today's Weather. Tomorrow's History. A Lifetime of Discovery.
          </p>
          <p className="text-[color:var(--np-text-2)] max-w-2xl mx-auto text-lg">
            Every observation matters. Every location has a story. Every day adds another page to the history of our planet.
          </p>
          <div className="mt-8 font-mono-tech text-[10px] tracking-[0.4em] uppercase text-[color:var(--np-muted)]">
            ONE WORLD · ONE SKY · ONE RECORD · ONE FUTURE
          </div>
          <a href="/weather-almanac" className="inline-flex items-center gap-2 mt-10 font-mono-tech text-[11px] tracking-[0.3em] uppercase border border-black/20 hover:bg-[color:var(--np-text)] hover:text-[color:var(--np-bg)] px-6 py-4 transition-colors">
            Explore the Weather Almanac →
          </a>
        </motion.div>
      </section>
    </TranslatedProse>
  );
}
